import { getFunctions, httpsCallable } from 'firebase/functions'
import { loadStripe } from '@stripe/stripe-js'
import { STRIPE_CONFIG } from '../config/stripe'

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_CONFIG.PUBLIC_KEY)

interface CheckoutSessionResponse {
    sessionId: string
}

export const PaymentService = {
    /**
     * Creates a checkout session and redirects to Stripe
     */
    async startStripeCheckout(priceId: string, userId: string): Promise<void> {
        try {
            const functions = getFunctions()
            const createStripeCheckout = httpsCallable<
                { priceId: string; successUrl: string; cancelUrl: string },
                CheckoutSessionResponse
            >(functions, 'createStripeCheckout')

            // Call Firebase Cloud Function
            const { data } = await createStripeCheckout({
                priceId,
                successUrl: STRIPE_CONFIG.SUCCESS_URL,
                cancelUrl: STRIPE_CONFIG.CANCEL_URL
            })

            if (!data.sessionId) {
                throw new Error('No session ID returned from backend')
            }

            // Redirect to Stripe
            const stripe = await stripePromise
            if (!stripe) {
                throw new Error('Stripe failed to initialize')
            }

            const { error } = await stripe.redirectToCheckout({
                sessionId: data.sessionId
            })

            if (error) {
                throw error
            }

        } catch (error) {
            console.error('Error starting checkout:', error)
            throw error
        }
    },

    /**
     * Creates a preference and redirects to MercadoPago (for one-time payments)
     * @deprecated Use startMercadoPagoSubscription for recurring subscriptions
     */
    async startMercadoPagoCheckout(title: string, price: number, userId: string): Promise<void> {
        try {
            const functions = getFunctions()
            const createMercadoPagoPreference = httpsCallable<
                { title: string; price: number; userId: string; successUrl: string; failureUrl: string },
                { initPoint: string }
            >(functions, 'createMercadoPagoPreference')

            const { data } = await createMercadoPagoPreference({
                title,
                price,
                userId,
                successUrl: `${window.location.origin}/payment/success`,
                failureUrl: `${window.location.origin}/payment/cancel`
            })

            if (data.initPoint) {
                window.location.href = data.initPoint
            } else {
                throw new Error('No initPoint returned from backend')
            }

        } catch (error) {
            console.error('Error starting MercadoPago checkout:', error)
            throw error
        }
    },

    /**
     * Creates a MercadoPago subscription (preapproval) and redirects to payment
     */
    async startMercadoPagoSubscription(interval: 'month' | 'year'): Promise<void> {
        try {
            const functions = getFunctions()
            const createMercadoPagoSubscription = httpsCallable<
                { interval: 'month' | 'year' },
                { initPoint: string; preapprovalId: string }
            >(functions, 'createMercadoPagoSubscription')

            const { data } = await createMercadoPagoSubscription({
                interval
            })

            if (data.initPoint) {
                window.location.href = data.initPoint
            } else {
                throw new Error('No initPoint returned from backend')
            }

        } catch (error) {
            console.error('Error starting MercadoPago subscription:', error)
            throw error
        }
    },

    /**
     * Cancels a MercadoPago subscription
     */
    async cancelMercadoPagoSubscription(): Promise<void> {
        try {
            const functions = getFunctions()
            const cancelMercadoPagoSubscription = httpsCallable<
                {},
                { success: boolean; message: string }
            >(functions, 'cancelMercadoPagoSubscription')

            const { data } = await cancelMercadoPagoSubscription({})

            if (!data.success) {
                throw new Error(data.message || 'Failed to cancel subscription')
            }

        } catch (error) {
            console.error('Error cancelling MercadoPago subscription:', error)
            throw error
        }
    },

    /**
     * Helps identifying if the user has an active premium subscription
     * utilizing the custom claims (if implemented) or user profile data
     */
    async redirectToCustomerPortal(): Promise<void> {
        try {
            const functions = getFunctions()
            const createPortalSession = httpsCallable<
                { returnUrl: string },
                { url: string }
            >(functions, 'createPortalSession')

            const { data } = await createPortalSession({
                returnUrl: window.location.origin
            })

            window.location.href = data.url
        } catch (error) {
            console.error('Error diverting to customer portal:', error)
            throw error
        }
    }
}
